import { useState, useEffect, useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import Header from 'components/Layouts/components/Header';
import BusinessSidebar from './components/BusinessSidebar';
import { useLocation, useNavigate } from 'react-router-dom';
import PaymentCardWarningModal, { PAYMENT_CARD_REMINDER_COOKIE } from '@/components/Common/PaymentCardWarningModal';
import PaymentDueModal from '@/components/Common/PaymentDueModal';
import { useGetCardDetails, usePayDues } from '@/apis/business/setting/billing/hook';
import { useGetBusinessProfile } from '@/apis/business/hook';
import { getCookie } from '@/utils/helperFunctions';
import { useGetCurrentUser } from '@/apis/user/hook';

type Props = {
  readonly children: JSX.Element;
};

export default function BusinessLayout({ children }: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isBillingPage = location.pathname.includes('billing');
  const [showPaymentWarning, setShowPaymentWarning] = useState(false);
  const [showPaymentDue, setShowPaymentDue] = useState(false);
  const { mutate: payDues, isPending: payingDues } = usePayDues();
  const { data: user, isLoading: loadingUser } = useGetCurrentUser();

  const { data: businessProfile } = useGetBusinessProfile();
  const { data: cardDetails, isLoading: isLoadingCards } = useGetCardDetails({
    enabled: Boolean(businessProfile?.agency_billing_enabled)
  });

  const memoizedAccountStatus = useMemo(() => businessProfile?.account_status, [businessProfile?.account_status]);
  // we need to restrict business status behavior for agency user
  const accountStatus =
    loadingUser || ['agency_admin', 'agency_admin_readonly', 'agent_builder'].includes(user?.role ?? '')
      ? 'active'
      : memoizedAccountStatus;
  const isPastDue = accountStatus === 'past_due';
  const isInactiveOrArchived = accountStatus === 'inactive' || accountStatus === 'archived';
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handlePayNow = () => {
    payDues(undefined, {
      onSuccess: () => {
        setShowPaymentDue(false);
      }
    });
  };

  useEffect(() => {
    if (isInactiveOrArchived && location.pathname !== '/access-denied') {
      navigate('/access-denied', { replace: true });
    }
  }, [isInactiveOrArchived, location.pathname, navigate]);

  useEffect(() => {
    if (isPastDue && location.pathname !== '/setting/billing') {
      navigate('/setting/billing', { replace: true });
    }
  }, [isPastDue, location.pathname, navigate]);

  useEffect(() => {
    if (isPastDue) {
      setShowPaymentDue(true);
      setShowPaymentWarning(false);
    } else {
      setShowPaymentDue(false);
    }
  }, [isPastDue]);

  useEffect(() => {
    if (
      showPaymentDue ||
      loadingUser ||
      ['agency_admin', 'agency_admin_readonly', 'agent_builder'].includes(user?.role!)
    )
      return;
    const isReminderDismissed = getCookie(PAYMENT_CARD_REMINDER_COOKIE);
    const hasNoCards = !isLoadingCards && (!cardDetails?.data || cardDetails?.data?.length === 0);
    const isBillingEnabled = Boolean(businessProfile?.agency_billing_enabled);
    const hasBillingPlan = Boolean(businessProfile?.business_plan_id && !businessProfile?.hide_billing);

    if (isBillingEnabled && hasBillingPlan && hasNoCards && !isReminderDismissed && !isBillingPage) {
      setShowPaymentWarning(true);
    } else {
      setShowPaymentWarning(false);
    }
  }, [cardDetails, isLoadingCards, businessProfile, isBillingPage, showPaymentDue, user?.role, loadingUser]);

  return (
    <>
      <Header onMenuClick={handleDrawerToggle} />
      <Box bgcolor={theme.palette.background.default} display='flex' pt='66px'>
        <BusinessSidebar disabled={isPastDue} isMenuOpen={mobileOpen} handleClose={handleDrawerToggle} />

        <Box
          component='main'
          sx={{
            flexGrow: 1,
            padding: { xs: 2, lg: 3 },
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          {children}
        </Box>
      </Box>

      <PaymentDueModal
        open={showPaymentDue}
        onClose={() => setShowPaymentDue(false)}
        onPayNow={handlePayNow}
        paying={payingDues}
      />
      {/* Secondary modal */}
      <PaymentCardWarningModal
        open={showPaymentWarning}
        onClose={() => setShowPaymentWarning(false)}
        billingType='business'
      />
    </>
  );
}
